import { Severity } from '@sentry/types';

import { SentryLogger } from '../../../../utils/SentryLogger';

import {
  PAYONEER_CHECKOUT_SDK_VERSION,
  PAYONEER_CHECKOUT_WEB_ENV,
  PAYONEER_CHECKOUT_WEB_SDK_INTEGRITY,
  PAYONEER_CHECKOUT_WEB_SDK_URL,
  PAYONEER_CHECKOUT_WEB_SDK_VERSION,
  payButtonContainerId,
  payButtonId,
  paymentContainerId,
  paymentNetworksId
} from './payoneerCheckoutPaymentConstants';
import {
  PayoneerCheckoutPaymentBeforeOtherWidgetRun,
  PayoneerCheckoutPaymentListUrl,
  PayoneerCheckoutPaymentLongId
} from './PayoneerCheckoutPayment.types';

interface PayoneerCheckoutWebInstance {
  state: 'LOADED' | 'ERROR';
  dropIn: (
    name: string,
    options?: Record<string, unknown>
  ) => { mount: (container: HTMLElement) => unknown };
  isDroppedIn: (name: string) => boolean;
  remove: (name: string) => boolean;
  updateLongId: (longId: string) => void;
}

interface PayoneerGlobal {
  CheckoutWeb: (configs: {
    env: 'live' | 'test';
    longId: string;
  }) => Promise<PayoneerCheckoutWebInstance>;
}

declare global {
  interface Window {
    Payoneer?: PayoneerGlobal;
  }
}

interface PayoneerCheckoutPaymentRunWidgetOptions {
  longId?: PayoneerCheckoutPaymentLongId;
  listUrl?: PayoneerCheckoutPaymentListUrl;
  theme?: string;
  widgetId?: string;
  beforeOtherWidgetRun?: PayoneerCheckoutPaymentBeforeOtherWidgetRun;
}

const sentryLogger = new SentryLogger('payoneer-checkout', {
  defaultTags: { 'payoneer-checkout-sdk': PAYONEER_CHECKOUT_SDK_VERSION }
});

class PayoneerCheckoutPayment {
  static beforeOtherWidgetRun: () => void | undefined;
  static currentWidgetId: string;
  static currentCheckout: PayoneerCheckoutWebInstance | null = null;
  static sdkPromise: Promise<PayoneerGlobal> | null = null;

  static loadWebSdk(): Promise<PayoneerGlobal> {
    if (typeof window === 'undefined') {
      return Promise.reject(new Error('Payoneer SDK requires a browser'));
    }
    if (window.Payoneer) {
      sentryLogger.breadcrumb('loadWebSdk: SDK already present on window');
      return Promise.resolve(window.Payoneer);
    }
    if (PayoneerCheckoutPayment.sdkPromise) {
      sentryLogger.breadcrumb('loadWebSdk: reusing in-flight SDK loader');
      return PayoneerCheckoutPayment.sdkPromise;
    }

    sentryLogger.breadcrumb('loadWebSdk: injecting SDK script', {
      sdkUrl: PAYONEER_CHECKOUT_WEB_SDK_URL,
      sdkVersion: PAYONEER_CHECKOUT_WEB_SDK_VERSION,
      env: PAYONEER_CHECKOUT_WEB_ENV
    });

    PayoneerCheckoutPayment.sdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = PAYONEER_CHECKOUT_WEB_SDK_URL;
      script.integrity = PAYONEER_CHECKOUT_WEB_SDK_INTEGRITY;
      script.crossOrigin = 'anonymous';
      script.async = true;
      script.onload = () => {
        if (window.Payoneer) {
          sentryLogger.breadcrumb('loadWebSdk: SDK script loaded');
          resolve(window.Payoneer);
        } else {
          sentryLogger.breadcrumb(
            'loadWebSdk: script loaded but window.Payoneer is missing',
            { sdkUrl: PAYONEER_CHECKOUT_WEB_SDK_URL },
            Severity.Error
          );
          reject(new Error('Payoneer SDK loaded but global is missing'));
        }
      };
      script.onerror = () => {
        PayoneerCheckoutPayment.sdkPromise = null;
        sentryLogger.breadcrumb(
          'loadWebSdk: script failed to load',
          {
            sdkUrl: PAYONEER_CHECKOUT_WEB_SDK_URL,
            integrity: PAYONEER_CHECKOUT_WEB_SDK_INTEGRITY
          },
          Severity.Error
        );
        reject(new Error('Failed to load Payoneer Checkout Web SDK'));
      };
      document.head.appendChild(script);
    });

    return PayoneerCheckoutPayment.sdkPromise;
  }

  static async runWidget({
    longId,
    listUrl,
    theme,
    widgetId,
    beforeOtherWidgetRun
  }: PayoneerCheckoutPaymentRunWidgetOptions): Promise<void> {
    sentryLogger.breadcrumb('runWidget: invoked', {
      sdkVersion: PAYONEER_CHECKOUT_SDK_VERSION,
      widgetId,
      previousWidgetId: PayoneerCheckoutPayment.currentWidgetId,
      sameWidget: PayoneerCheckoutPayment.currentWidgetId === widgetId,
      hasLongId: Boolean(longId),
      hasListUrl: Boolean(listUrl),
      theme
    });

    if (PayoneerCheckoutPayment.currentWidgetId !== widgetId) {
      try {
        PayoneerCheckoutPayment.beforeOtherWidgetRun?.();
      } catch (error) {
        sentryLogger.captureException(error, {
          stage: 'before-other-widget-run',
          extra: {
            widgetId,
            previousWidgetId: PayoneerCheckoutPayment.currentWidgetId
          }
        });
      }
      PayoneerCheckoutPayment.cleanup();
      PayoneerCheckoutPayment.currentWidgetId = widgetId;
    }
    PayoneerCheckoutPayment.beforeOtherWidgetRun = beforeOtherWidgetRun;

    try {
      if (PAYONEER_CHECKOUT_SDK_VERSION === 'web') {
        if (!longId) {
          sentryLogger.breadcrumb(
            'runWidget: skipping web run, longId is missing',
            { widgetId },
            Severity.Warning
          );
          return;
        }
        await PayoneerCheckoutPayment.runWebWidget(longId);
      } else {
        if (!listUrl) {
          sentryLogger.breadcrumb(
            'runWidget: skipping legacy run, listUrl is missing',
            { widgetId },
            Severity.Warning
          );
          return;
        }
        await PayoneerCheckoutPayment.runLegacyWidget(listUrl, theme);
      }
    } catch (error) {
      sentryLogger.captureException(error, {
        stage: 'run-widget',
        extra: {
          sdkVersion: PAYONEER_CHECKOUT_SDK_VERSION,
          env: PAYONEER_CHECKOUT_WEB_ENV,
          widgetId,
          theme,
          hasLongId: Boolean(longId),
          hasListUrl: Boolean(listUrl)
        }
      });
      throw error;
    }
  }

  static async runWebWidget(longId: string): Promise<void> {
    sentryLogger.breadcrumb('runWebWidget: starting', {
      env: PAYONEER_CHECKOUT_WEB_ENV,
      longId
    });

    const Payoneer = await PayoneerCheckoutPayment.loadWebSdk();

    sentryLogger.breadcrumb('runWebWidget: calling Payoneer.CheckoutWeb', {
      env: PAYONEER_CHECKOUT_WEB_ENV,
      longId
    });

    const checkout = await Payoneer.CheckoutWeb({
      env: PAYONEER_CHECKOUT_WEB_ENV,
      longId
    });

    sentryLogger.breadcrumb('runWebWidget: CheckoutWeb instance created', {
      state: checkout?.state
    });

    if (checkout.state !== 'LOADED') {
      sentryLogger.captureMessage(
        'Payoneer CheckoutWeb returned non-LOADED state',
        {
          stage: 'checkout-state',
          level: Severity.Error,
          tags: { 'payoneer-checkout-state': String(checkout?.state) },
          extra: {
            state: checkout?.state,
            env: PAYONEER_CHECKOUT_WEB_ENV,
            longId
          }
        }
      );
      return;
    }

    const container = document.getElementById(paymentContainerId);
    if (!container) {
      sentryLogger.captureMessage(
        'Payoneer mount container is not present in DOM',
        {
          stage: 'mount-container',
          level: Severity.Error,
          extra: { paymentContainerId, longId }
        }
      );
      return;
    }

    sentryLogger.breadcrumb('runWebWidget: mounting cards drop-in', {
      paymentContainerId
    });

    checkout.dropIn('cards', {}).mount(container);

    sentryLogger.breadcrumb('runWebWidget: cards widget mounted');

    PayoneerCheckoutPayment.currentCheckout = checkout;
  }

  static async runLegacyWidget(listUrl: string, theme?: string): Promise<void> {
    sentryLogger.breadcrumb('runLegacyWidget: starting', { listUrl, theme });

    const imported = await import('op-payment-widget-v3');

    sentryLogger.breadcrumb(
      'runLegacyWidget: op-payment-widget-v3 module loaded'
    );

    imported.default(paymentNetworksId, {
      payButton: payButtonId,
      payButtonContainer: payButtonContainerId,
      widgetCssUrl:
        theme === 'dark'
          ? window.location.origin + '/css/payoneer-checkout-widget-dark.css'
          : window.location.origin + '/css/payoneer-checkout-widget-light.css',
      listUrl,
      smartSwitch: true
    });

    sentryLogger.breadcrumb('runLegacyWidget: widget initialized');
  }

  static cleanup(): void {
    const checkout = PayoneerCheckoutPayment.currentCheckout;
    try {
      if (checkout?.isDroppedIn('cards')) {
        sentryLogger.breadcrumb('cleanup: removing cards widget');
        checkout.remove('cards');
      }
    } catch (error) {
      sentryLogger.captureException(error, { stage: 'cleanup' });
    }
    PayoneerCheckoutPayment.currentCheckout = null;
  }
}

export default PayoneerCheckoutPayment;
